"use client"

import styled from "@emotion/styled"
import { GetComponentQuery } from "@eurodrive/apollo"
import { Desktop, Mobile } from "@eurodrive/ui"
import {
  extractProperty,
  filterPublishedBranches,
  findBranchImage,
} from "@eurodrive/utils"
import { getUmbracoMediaUrl } from "@eurodrive/utils/getUmbracoMediaUrl"
import { branchLocatorClicked } from "@eurodrive/utils/gtm"
import { Typography } from "@mui/material"
import { APIProvider, Map, useMap } from "@vis.gl/react-google-maps"
import { BranchDto } from "api"
import Image from "next/image"
import Link from "next/link"
import { useSearchParams } from "next/navigation"
import { useEffect, useMemo, useRef, useState } from "react"
import CustomMarker from "./CustomMarket"
import MobileMapComponent from "./MobileMap"

const API_KEY = process.env.GOOGLE_API_KEY

const StyledGrid = styled.div`
  display: grid;
  grid-template-columns: 545px 1fr;
  height: 840px;
  width: 100%;
`

const BranchesColumn = styled.div<{ $more: boolean }>`
  display: flex;
  flex-direction: column;
  padding-top: 32px;
  height: 100%;
  overflow-y: hidden;
  ${({ $more }) => $more && `padding-top: 0px;`}
`

const BranchesBox = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow-y: auto;
`

const BranchCard = styled.div`
  display: flex;
  padding: 16px 0px;
  gap: 32px;
  border: 1px solid #ece9e9;
`

const InnerCard = styled.div`
  display: flex;
  padding: 0px 32px;
  align-items: center;
  gap: 12px;
  width: 100%;
`

const ImageContainer = styled.div`
  width: 166px;
  height: 170px;
  position: relative;
`

const ContentBox = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 8px;
`

const DetailsBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const DetailsRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`

const TimeColumn = styled.div`
  display: flex;
  flex-direction: column;
`

const StyledLink = styled(Link)`
  text-decoration: none;
  border-bottom: 1px solid #000;
  display: flex;
`

const MoreBox = styled.div`
  padding: 32px 32px 24px 32px;
`

const MobileLayout = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  padding: 32px;
`

function getTime(time: string) {
  const [hours, minutes] = time.split(":")
  const formattedTime = `${hours}.${minutes}` // "20.00"

  return formattedTime
}

// MapController component to handle map pan and zoom
function MapController({
  selectedBranch,
}: {
  selectedBranch: BranchDto | null
}) {
  const map = useMap()

  useEffect(() => {
    if (map && selectedBranch) {
      map.panTo({
        lat: selectedBranch.latitude,
        lng: selectedBranch.longitude,
      })
    }
  }, [map, selectedBranch])

  return null
}

export default function MapComponent({
  branches,
  branchesUmbraco,
}: {
  branches: BranchDto[]
  branchesUmbraco: GetComponentQuery
}) {
  const brancheImages = useMemo(
    () =>
      (branchesUmbraco.components?.edges || []).map((branch) => {
        {
          const object = branch.node
          const branchNumber = extractProperty(object, "branchNumber")
          const thumbnail = extractProperty(object, "thumbnail")
          const thumbnailMobile = extractProperty(object, "thumbnailMobile")
          return {
            branchNumber,
            thumbnail,
            thumbnailMobile,
          }
        }
      }) as {
        branchNumber: string
        thumbnail: string
        thumbnailMobile: string
      }[],
    [branchesUmbraco],
  )

  const publishedBranches = useMemo(
    () => filterPublishedBranches(branches, brancheImages),
    [branches, brancheImages],
  )

  const searchParams = useSearchParams()
  const [selectedBranch, setSelectedBranch] = useState<BranchDto | null>(null)
  const mapContainerRef = useRef<HTMLDivElement>(null)

  // Pre-select branch from URL query parameter
  useEffect(() => {
    const branchIdParam = searchParams.get("branchId")
    if (branchIdParam) {
      const branch = publishedBranches.find(
        (b) => b.id === Number(branchIdParam),
      )
      if (branch) {
        setSelectedBranch(branch)
        // Smooth scroll to map section with offset
        setTimeout(() => {
          if (mapContainerRef.current) {
            const elementPosition =
              mapContainerRef.current.getBoundingClientRect().top
            const offsetPosition = elementPosition + window.pageYOffset - 80

            window.scrollTo({
              top: offsetPosition,
              behavior: "smooth",
            })
          }
        }, 100)
      }
    }
  }, [searchParams, publishedBranches])

  const handleBranchSelect = (branch: BranchDto) => {
    branchLocatorClicked(branch.branchName, "branches_page")
    setSelectedBranch(selectedBranch?.id === branch.id ? null : branch)
  }
  const more = publishedBranches.length > 4

  // undefined when there are no branches at all, in which case the map is skipped
  // rather than handed an invalid centre.
  const mapCenterBranch = publishedBranches[0]
  return (
    <>
      <Desktop>
        <StyledGrid ref={mapContainerRef}>
          <BranchesColumn $more={more}>
            {more && (
              <MoreBox>
                <Typography component="h2">
                  {publishedBranches.length} סניפים בפריסה ארצית
                </Typography>
              </MoreBox>
            )}
            <BranchesBox as="ul" role="list" aria-label="רשימת מיקומי סניפים">
              {publishedBranches.map((branch) => (
                <BranchCard
                  as="li"
                  key={branch.id}
                  onClick={() => handleBranchSelect(branch)}
                  style={{
                    cursor: "pointer",
                    background:
                      selectedBranch?.id === branch.id ? "#f5f5f5" : "white",
                  }}
                >
                  <InnerCard as="article">
                    <ContentBox>
                      <Typography variant="h3" component="h3">
                        {branch.branchName}
                      </Typography>
                      <DetailsBox as="address" style={{ fontStyle: "normal" }}>
                        <DetailsRow>
                          <Image
                            src={"/icons/map.svg"}
                            alt="מיקום"
                            width={18}
                            height={18}
                          />
                          <Typography variant="body3">
                            {branch.street} {branch.houseNumber}, {branch.city}
                          </Typography>
                        </DetailsRow>
                        <DetailsRow>
                          <Image
                            src={"/icons/clock.svg"}
                            alt="שעות פעילות"
                            width={18}
                            height={18}
                          />
                          <TimeColumn>
                            <Typography variant="body3" component="div">
                              <span>א׳ - ה׳:</span>{" "}
                              <time dateTime={branch.regularDayOpeningHours}>
                                {getTime(branch.regularDayOpeningHours)}
                              </time>
                              {" - "}
                              <time dateTime={branch.regularDayClosingHours}>
                                {getTime(branch.regularDayClosingHours)}
                              </time>
                            </Typography>
                            <Typography variant="body3" component="div">
                              <span>ו׳:</span>{" "}
                              <time dateTime={branch.holidayDayOpeningHours}>
                                {getTime(branch.holidayDayOpeningHours)}
                              </time>
                              {" - "}
                              <time dateTime={branch.holidayDayClosingHours}>
                                {getTime(branch.holidayDayClosingHours)}
                              </time>
                            </Typography>
                          </TimeColumn>
                        </DetailsRow>
                        <DetailsRow>
                          <Image
                            src={"/icons/email.svg"}
                            alt="אימייל"
                            width={18}
                            height={18}
                          />
                          <StyledLink
                            href={`mailto:${branch.emailAddress}`}
                            aria-label={`כתובת אימייל: ${branch.emailAddress}`}
                            onClick={(e) => {
                              e.stopPropagation()
                              branchLocatorClicked(
                                branch.branchName,
                                "branches_page",
                              )
                            }}
                          >
                            <Typography variant="body3">
                              {branch.emailAddress}
                            </Typography>
                          </StyledLink>
                        </DetailsRow>
                        <DetailsRow>
                          <Image
                            src={"/icons/phone.svg"}
                            alt="טלפון"
                            width={18}
                            height={18}
                          />
                          <StyledLink
                            href={`tel:${branch.phoneNumbers[0]}`}
                            aria-label={`מספר טלפון: ${branch.phoneNumbers[0]}`}
                            onClick={(e) => {
                              e.stopPropagation()
                              branchLocatorClicked(
                                branch.branchName,
                                "branches_page",
                              )
                            }}
                          >
                            <Typography variant="body3">
                              {branch.phoneNumbers[0]}
                            </Typography>
                          </StyledLink>
                        </DetailsRow>
                      </DetailsBox>
                    </ContentBox>
                    {!!getUmbracoMediaUrl(
                      findBranchImage(brancheImages, branch)?.thumbnail || "",
                    ) && (
                      <ImageContainer>
                        <Image
                          alt={`תמונת סניף ${branch.branchName}`}
                          src={getUmbracoMediaUrl(
                            findBranchImage(brancheImages, branch)?.thumbnail ||
                              "",
                          )}
                          fill
                        />
                      </ImageContainer>
                    )}
                  </InnerCard>
                </BranchCard>
              ))}
            </BranchesBox>
          </BranchesColumn>
          <div aria-hidden="true" tabIndex={-1} role="presentation">
            <APIProvider apiKey={API_KEY!}>
              {mapCenterBranch && (
                <Map
                  mapTypeControl={false}
                  defaultCenter={{
                    lat: mapCenterBranch.latitude,
                    lng: mapCenterBranch.longitude,
                  }}
                  defaultZoom={10}
                  mapId="c4cbf2b3fb26b849b070fc87"
                >
                  <MapController selectedBranch={selectedBranch} />
                  {publishedBranches.map((branch) => (
                    <CustomMarker
                      key={branch.id}
                      branch={branch}
                      isSelected={selectedBranch?.id === branch.id}
                      onClick={() => handleBranchSelect(branch)}
                      branchImage={
                        findBranchImage(brancheImages, branch)?.thumbnail
                      }
                    />
                  ))}
                </Map>
              )}
            </APIProvider>
          </div>
        </StyledGrid>
      </Desktop>
      <Mobile fullWidth>
        <MobileMapComponent
          branches={publishedBranches}
          brancheImages={brancheImages}
        />
      </Mobile>
    </>
  )
}
